/* eslint-disable @typescript-eslint/no-explicit-any */
import { AddToCartType } from '@/constants'
import { useCart } from '@/containers'
import type { Product } from '@/types'
import { Dialog } from '@headlessui/react'
import isEmpty from 'lodash/isEmpty'
import dynamic from 'next/dynamic'
import { useEffect, useMemo, useState } from 'react'
import AddToCartSuccess from '../AddToCartSuccess/AddToCartSuccess'
import { CartOutIcon } from '../Icon/CartOutIcon'
import ProductAffiliateCashPromotion from '../ProductAffiliateCashPromotion'
import {
  FlashSaleProductQuantity,
  ProductCardImage,
  ProductCardLabels,
  ProductCardName,
  ProductPrice,
  ProductRate,
  ProductVoucher,
} from './components'
import HighlightProperties from './components/HighlightProperties'

const FlashSaleProductCountdown = dynamic(
  () => import('./components/FlashSaleProductCountdown'),
  {
    ssr: false,
  }
)

interface ProductCardProps {
  product: Product
  className?: string
  hideFlashSaleCountdown?: boolean
  hideQuantity?: boolean
  hideVoucher?: boolean
}

const ProductCard = ({
  className,
  product,
  hideFlashSaleCountdown,
  hideQuantity,
  hideVoucher,
}: ProductCardProps) => {
  const { addToCart } = useCart()
  const [openAddToCartModal, setOpenAddToCartModal] = useState(false)

  const renderProperties =
    product.propertyPositionInList === 'top' ||
    product.propertyPositionInList === 'right'
      ? product.properties
          ?.filter(x => (x.values?.length || !isEmpty(x.value)) && x.showInList)
          .sort((x, y) => (x?.order || 0) - (y?.order || 0))
          .slice(0, 3)
      : []

  const reviewsCount = useMemo(() => {
    if (product?.reviewSummary) {
      return product?.reviewSummary.reduce(
        (acc: number, curr: number) => acc + curr,
        0
      )
    }
    return 0
  }, [product])

  useEffect(() => {
    if (!openAddToCartModal) {
      return
    }

    const timeout = setTimeout(() => {
      setOpenAddToCartModal(false)
    }, 5000)

    return () => clearTimeout(timeout)
  }, [openAddToCartModal])

  const showProperty = renderProperties?.length
  const position =
    showProperty && product.propertyPositionInList === 'top' ? 'top' : 'right'

  return (
    <div className={`${className} group/card relative rounded-sm bg-white p-3`}>
      <div className='relative pb-8'>
        {showProperty ? (
          <div
            className={`relative flex ${
              position === 'top' ? 'flex-col-reverse' : ''
            } aspect-square`}
          >
            <ProductCardImage
              slugs={product.slugs}
              thumbnail={product.thumbnail}
              name={product.name}
              className={`${
                position === 'top' ? 'mx-auto' : ''
              }  flex w-3/4 items-center justify-center`}
            />
            <HighlightProperties
              position={position || 'top'}
              data={renderProperties}
            />
          </div>
        ) : (
          <ProductCardImage
            slugs={product.slugs}
            thumbnail={product.thumbnail}
            name={product.name}
          />
        )}
        <ProductCardLabels labels={product.labels} />
        {!hideFlashSaleCountdown && product.flashSale?.endTime && (
          <FlashSaleProductCountdown
            endTime={product.flashSale?.endTime}
            productId={product._id}
            className={`absolute bottom-9 -translate-x-1/2 ${
              showProperty && position !== 'top' ? 'left-[37.5%]' : 'left-1/2'
            }`}
          />
        )}
      </div>
      <ProductCardName
        name={product.name}
        slugs={product.slugs}
        className='mt-2'
      />
      <ProductPrice
        finalPrice={product.finalPrice}
        originPrice={product.price}
        className='mt-2'
        stockStatus={product.stockStatus}
      />
      {product.bonusAmount ? (
        <ProductAffiliateCashPromotion
          className='mt-1'
          amount={product.bonusAmount}
        />
      ) : (
        <ProductRate
          rate={product.average}
          reviewsCount={reviewsCount}
          className='mt-1'
        />
      )}
      {!hideQuantity && (
        <FlashSaleProductQuantity
          soldQuantity={product.flashSale?.soldQuantity}
          totalQuantity={product.flashSale?.quantity}
          className='mt-3'
        />
      )}
      {!hideVoucher && (
        <ProductVoucher coupons={product.coupons} className='mt-2' />
      )}
      <div className='cart-button'>
        <button
          className='absolute bottom-0 right-0 rounded-br-lg rounded-tl-lg bg-primary p-4'
          onClick={() =>
            addToCart(product, AddToCartType.ADD).then(() =>
              setOpenAddToCartModal(true)
            )
          }
        >
          <CartOutIcon />
        </button>
      </div>
      <Dialog
        open={openAddToCartModal}
        onClose={() => setOpenAddToCartModal(false)}
      >
        <AddToCartSuccess />
      </Dialog>
    </div>
  )
}

export default ProductCard
